import React, { useEffect, useState, useCallback, useMemo } from "react";
import { GoogleMap, DirectionsService, DirectionsRenderer, useJsApiLoader } from "@react-google-maps/api";
import { formatCurrency } from '../utils/currencyFormatter';

const libraries = ["places"];

const BookingSummary = ({
  pickupLocation,
  dropoffLocation,
  stops,
  selectedVehicle,
  addons,
  totalPrice,
  isReturnBooking,
  returnPickupLocation,
  returnDropoffLocation,
  returnStops,
  returnSelectedVehicle,
  returnAddons,
  returnTotalPrice,
  step,
  couponCode,
  couponDiscount,
}) => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
    libraries,
  });

  const [outboundResponse, setOutboundResponse] = useState(null);
  const [returnResponse, setReturnResponse] = useState(null);
  const [mapCenter, setMapCenter] = useState({ lat: -37.8136, lng: 144.9631 });
  const [mapZoom, setMapZoom] = useState(10);

  const [isAddressExpanded, setIsAddressExpanded] = useState(false);
  const [isReturnAddressExpanded, setIsReturnAddressExpanded] = useState(false);


  const safeAddons = useMemo(() => addons || {}, [addons]);
  const safeReturnAddons = useMemo(() => returnAddons || {}, [returnAddons]);

  const stepNumber = useMemo(() => {
    if (typeof step === "number") return step;
    const path = String(step || "").toLowerCase();
    if (path.includes("/booking/return-vehicle-selection")) return 6;
    if (path.includes("/booking/return-trip")) return 5;
    if (path.includes("/booking/payment")) return 4;
    if (path.includes("/booking/personal-details")) return 3;
    if (path.includes("/booking/vehicle-selection")) return 2;
    return 1;
  }, [step]);

  const hasAddons = useCallback((a) => {
    if (!a || typeof a !== "object") return false;
    const bs = a.babySeatDetails || {};
    const infant = bs.infant || {};
    const child = bs.child || {};
    const booster = bs.booster || {};

    const infantChosen = !!infant.selected || (Number(infant.quantity) || 0) > 0 || (Number(infant.totalPrice) || 0) > 0;
    const childChosen = !!child.selected || (Number(child.quantity) || 0) > 0 || (Number(child.totalPrice) || 0) > 0;
    const boosterChosen = !!booster.selected || (Number(booster.quantity) || 0) > 0 || (Number(booster.totalPrice) || 0) > 0;
    const bsTotal = Number(bs.totalBabySeatPrice) || 0;
    const babySeatChosen = !!a.babySeat || infantChosen || childChosen || boosterChosen || bsTotal > 0;

    const wineChosen = !!a.wineBottle || (Number(a.wineBottlePrice) || 0) > 0;
    const redCarpetChosen = !!a.redCarpet || (Number(a.redCarpetPrice) || 0) > 0;

    return babySeatChosen || wineChosen || redCarpetChosen;
  }, []);

  const BabySeatBlock = ({ a }) => {
    const bs = (a && a.babySeatDetails) || {};
    const infant = bs.infant || {};
    const child = bs.child || {};
    const booster = bs.booster || {};

    const showInfant = !!infant.selected || (Number(infant.quantity) || 0) > 0 || (Number(infant.totalPrice) || 0) > 0;
    const showChild = !!child.selected || (Number(child.quantity) || 0) > 0 || (Number(child.totalPrice) || 0) > 0;
    const showBooster = !!booster.selected || (Number(booster.quantity) || 0) > 0 || (Number(booster.totalPrice) || 0) > 0;
    const totalBS = Number(bs.totalBabySeatPrice) || 0;

    if (!(a && (a.babySeat || showInfant || showChild || showBooster || totalBS > 0))) return null;

    return (
      <li className="list-group-item bg-light">
        <div className="d-flex align-items-center">
          <i className="bi bi-bag-plus me-2 text-primary"></i>
          <span><strong>Baby Seat:</strong></span>
        </div>
        <ul className="list-unstyled ps-4 mt-2">
          {showInfant && (
            <li className="d-flex justify-content-between">
              <span>Infant: {(Number(infant.quantity) || 0)} × {formatCurrency(Number(infant.price) || 0)}</span>
              <span className="fw-bold">
                {formatCurrency(Number(infant.totalPrice) || ((Number(infant.quantity) || 0) * (Number(infant.price) || 0)))}
              </span>
            </li>
          )}
          {showChild && (
            <li className="d-flex justify-content-between">
              <span>Child: {(Number(child.quantity) || 0)} × {formatCurrency(Number(child.price) || 0)}</span>
              <span className="fw-bold">
                {formatCurrency(Number(child.totalPrice) || ((Number(child.quantity) || 0) * (Number(child.price) || 0)))}
              </span>
            </li>
          )}
          {showBooster && (
            <li className="d-flex justify-content-between">
              <span>Booster: {(Number(booster.quantity) || 0)} × {formatCurrency(Number(booster.price) || 0)}</span>
              <span className="fw-bold">
                {formatCurrency(Number(booster.totalPrice) || ((Number(booster.quantity) || 0) * (Number(booster.price) || 0)))}
              </span>
            </li>
          )}
          {totalBS > 0 && (
            <li className="d-flex justify-content-between border-top mt-2 pt-2">
              <span className="fw-bold">Total Baby Seat Price:</span>
              <span className="fw-bold">{formatCurrency(totalBS)}</span>
            </li>
          )}
        </ul>
      </li>
    );
  };

  useEffect(() => {
    if (pickupLocation && dropoffLocation) {
      const geocoder = new window.google.maps.Geocoder();
      Promise.all([
        new Promise((resolve) => {
          geocoder.geocode({ address: pickupLocation }, (results, status) => {
            if (status === "OK" && results[0]) resolve(results[0].geometry.location);
            else resolve(null);
          });
        }),
        new Promise((resolve) => {
          geocoder.geocode({ address: dropoffLocation }, (results, status) => {
            if (status === "OK" && results[0]) resolve(results[0].geometry.location);
            else resolve(null);
          });
        }),
      ]).then(([pickupCoords, dropoffCoords]) => {
        if (pickupCoords && dropoffCoords) {
          const center = {
            lat: (pickupCoords.lat() + dropoffCoords.lat()) / 2,
            lng: (pickupCoords.lng() + dropoffCoords.lng()) / 2,
          };
          setMapCenter(center);
          setMapZoom(10);
        }
      });
    }
  }, [pickupLocation, dropoffLocation]);

  useEffect(() => {
    if (isReturnBooking && returnPickupLocation && returnDropoffLocation) {
      setReturnResponse(null);
    }
  }, [isReturnBooking, returnPickupLocation, returnDropoffLocation, returnStops]);

  const directionsCallback = useCallback((result, status, isReturn = false) => {
    if (status === "OK") {
      if (isReturn) {
        setReturnResponse(result);
      } else {
        setOutboundResponse(result);
      }
    }
  }, []);

  const renderMap = (origin, destination, stopsList, response, isReturn = false) => {
    if (!isLoaded) return <div>Loading Map...</div>;

    return (
      <GoogleMap
        mapContainerStyle={{ height: "170px", width: "100%" }}
        zoom={mapZoom}
        center={mapCenter}
        options={{
          disableDefaultUI: true,
          gestureHandling: "cooperative",
        }}
      >
        {origin && destination && !response && (
          <DirectionsService
            options={{
              destination,
              origin,
              waypoints: (stopsList || []).map((stop) => ({ location: stop, stopover: true })),
              travelMode: "DRIVING",
              optimizeWaypoints: true,
            }}
            callback={(result, status) => directionsCallback(result, status, isReturn)}
          />
        )}
        {response && <DirectionsRenderer directions={response} />}
      </GoogleMap>
    );
  };

  const grandTotal =
    (Number(totalPrice) || 0) +
    (isReturnBooking ? (Number(returnTotalPrice) || 0) : 0) -
    (Number(couponDiscount) || 0);

  const toggleAddressSheet = () => setIsAddressExpanded((s) => !s);
  const toggleReturnAddressSheet = () => setIsReturnAddressExpanded((s) => !s);

  return (
    <div className="">
      {pickupLocation && (
        <div className={`address-sheet ${isAddressExpanded ? "expanded" : ""}`}>
          <div
            className="d-flex justify-content-between align-items-center p-3 bg-white rounded shadow-sm mb-2"
            style={{ border: "1px solid #e0e0e0" }}
          >
            <div>
              <div className="fw-bold way-title">One-Way Trip Summary</div>
              <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                Total Price
              </div>
              <div className="text-muted" style={{ fontSize: "0.7rem" }}>
                Taxes and fees included
              </div>
            </div>
            <div className="d-flex align-items-center">
              <span className="fw-bold fs-4 text-success">
                {formatCurrency(Number(totalPrice) || 0)}
              </span>
              <button
                className="btn btn-link p-0 ms-2"
                onClick={toggleAddressSheet}
                title="Expand/Collapse"
              >
                <i className={`fa fa-chevron-${isAddressExpanded ? "up" : "down"}`}></i>
              </button>
            </div>
          </div>

          <div className="address-details" style={{ display: isAddressExpanded ? "block" : "none" }}>
            <div className="mb-3">
              {renderMap(pickupLocation, dropoffLocation, stops || [], outboundResponse)}
            </div>

            <div className="summary-grid">
              <div className="summary-item">
                <span className="summary-label">PICKUP LOCATION</span>
                <span className="summary-value">{pickupLocation}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">DROP-OFF LOCATION</span>
                <span className="summary-value">{dropoffLocation}</span>
              </div>
              {stops && stops.length > 0 && (
                <div className="summary-item">
                  <span className="summary-label">STOPS</span>
                  <span className="summary-value">{stops.join(", ")}</span>
                </div>
              )}
            </div>

            {stepNumber >= 2 && selectedVehicle && (
              <>
                <div className="d-flex align-items-center mb-2">
                  <span>
                    <strong>Vehicle:</strong> {selectedVehicle.Vehicle_Name}
                  </span>
                </div>

                {hasAddons(safeAddons) && (
                  <div className="mt-3">
                    <h5 className="text-primary mb-2">Add-ons:</h5>
                    <ul className="list-group list-group-flush">
                      <BabySeatBlock a={safeAddons} />

                      

                      
                    </ul>
                  </div>
                )}
              </>
            )}

            {stepNumber >= 2 && selectedVehicle && (
              <div className="d-flex justify-content-between align-items-center p-3 bg-primary text-white rounded-bottom">
                <span className="fw-bold">Trip Total:</span>
                <span className="fw-bold fs-5">{formatCurrency(Number(totalPrice) || 0)}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {isReturnBooking && returnPickupLocation && returnDropoffLocation && (
        <div className={`address-sheet ${isReturnAddressExpanded ? "expanded" : ""}`}>
          <div
            className="d-flex justify-content-between align-items-center p-3 bg-white rounded shadow-sm mb-2"
            style={{ border: "1px solid #e0e0e0" }}
          >
            <div>
              <div className="fw-bold">Return Trip Summary</div>
              <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                Total Price
              </div>
              <div className="text-muted" style={{ fontSize: "0.7rem" }}>
                Taxes and fees included
              </div>
            </div>
            <div className="d-flex align-items-center">
              <span className="fw-bold fs-4 text-success">
                {formatCurrency(Number(returnTotalPrice) || 0)}
              </span>
              <button
                className="btn btn-link p-0 ms-2"
                onClick={toggleReturnAddressSheet}
                title="Expand/Collapse"
              >
                <i className={`fa fa-chevron-${isReturnAddressExpanded ? "up" : "down"}`}></i>
              </button>
            </div>
          </div>

          <div className="address-details" style={{ display: isReturnAddressExpanded ? "block" : "none" }}>
            <div className="mb-3">
              {renderMap(returnPickupLocation, returnDropoffLocation, returnStops || [], returnResponse, true)}
            </div>

            <div className="summary-grid">
              <div className="summary-item">
                <span className="summary-label">PICKUP LOCATION</span>
                <span className="summary-value">{returnPickupLocation}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">DROP-OFF LOCATION</span>
                <span className="summary-value">{returnDropoffLocation}</span>
              </div>
              {returnStops && returnStops.length > 0 && (
                <div className="summary-item">
                  <span className="summary-label">STOPS</span>
                  <span className="summary-value">{returnStops.join(", ")}</span>
                </div>
              )}
            </div>

            {stepNumber >= 6 && returnSelectedVehicle && (
              <>
                <div className="d-flex align-items-center mb-2">
                  <span>
                    <strong>Vehicle:</strong> {returnSelectedVehicle.Vehicle_Name}
                  </span>
                </div>

                {hasAddons(safeReturnAddons) && (
                  <div className="mt-3">
                    <h5 className="text-primary mb-2">Add-ons:</h5>
                    <ul className="list-group list-group-flush">
                      <BabySeatBlock a={safeReturnAddons} />

                       

                      
                    </ul>
                  </div>
                )}
              </>
            )}

            {stepNumber >= 6 && returnSelectedVehicle && (
              <div className="d-flex justify-content-between align-items-center p-3 bg-primary text-white rounded-bottom">
                <span className="fw-bold">Trip Total:</span>
                <span className="fw-bold fs-5">{formatCurrency(Number(returnTotalPrice) || 0)}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {(Number(couponDiscount) > 0) && (
        <div className="address-sheet">
          <div className="address-header">
            <span className="address-header-bar"></span>
            <div className="address-title">Coupon Applied</div>
          </div>
          <div className="address-details">
            <div className="d-flex justify-content-between">
              <span><strong>Code:</strong> {couponCode}</span>
              <span className="text-success fw-bold">-{formatCurrency(Number(couponDiscount) || 0)}</span>
            </div>
          </div>
        </div>
      )}

      <div>
        <div className="address-details">
          <div className="d-flex justify-content-between align-items-center">
            <span className="address-title">Grand Total:</span>
            <span className="fw-bold fs-4">{formatCurrency(grandTotal)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingSummary;
